import React from 'react';
import _orderBy from 'lodash/orderBy';

import { IconPiano } from '@trqk/components';
import { locale, removeNulls } from '@trqk/utils';

import { DataCard, DataCardTable } from '../components';
import { TopWritersOrPublishersRow } from '..';
import { URL_PARAMS } from '../../../../../../constants';

interface TopWritersCardProps {
    composersSummary: Maybe<InsightComposersSummary>;
}

const earningsColor = 'quarternary';

/**
 * Shows an Insights card for the top writers in the past four statements. Note that although the
 * title of the card in the UI is 'Top Writers', the data we are reading is nominally for composers.
 * @param props - {@link TopWritersCardProps}
 * @returns JSX
 */
export const TopWritersCard = ({
    composersSummary,
}: TopWritersCardProps): JSX.Element => {
    const data = composersSummary?.data ?? null;
    const statement_id_end = composersSummary?.statement_id_end ?? null;
    const statement_id_start = composersSummary?.statement_id_start ?? null;

    const sortedAndSlicedData = _orderBy(
        removeNulls<InsightComposerSummaryData>(data ?? []),
        ['earnings', 'name'],
        ['desc', 'asc'],
    )
        .slice(0, 5)
        .map(
            (
                datum: InsightComposerSummaryData,
            ): InsightComposerSummaryData => ({
                ...datum,
                // For some reason, certain names show up lower cased in the insights
                // data and upper cased in the performance data, or with trailing spaces in the
                // performances data but not in the insights data. This is a band-aid to help the
                // filter value (which is set from this data) match the values in the performance
                // data.
                name: datum.name.trim().toUpperCase(),
            }),
        );

    return (
        <DataCard
            title={locale.enUS.INSIGHTS.CARDS.TOP_WRITERS.TITLE}
            color={earningsColor}
            header={
                <div className="mx-4 text-center leading-6 clamp-2-lines-break-words">
                    {locale.enUS.INSIGHTS.CARDS.TOP_WRITERS.HEADER.PART_1}{' '}
                    <span className="font-semibold">
                        {locale.enUS.INSIGHTS.CARDS.TOP_WRITERS.HEADER.PART_2}
                    </span>
                </div>
            }
            Icon={IconPiano}
            inInsightsIndex={false}
        >
            {sortedAndSlicedData.length === 0 && (
                <div className="mx-16 mt-20 italic text-tertiary-400 text-center text-sm">
                    {locale.enUS.INSIGHTS.CARDS.TOP_WRITERS.EMPTY_MESSAGE}
                </div>
            )}
            <DataCardTable>
                {sortedAndSlicedData.map(
                    (
                        datum: InsightComposerSummaryData,
                        index: number,
                    ): JSX.Element => (
                        <TopWritersOrPublishersRow
                            index={index}
                            // eslint-disable-next-line react/no-array-index-key -- the use can't sort this array, so using the index as the key is safe
                            key={index}
                            datum={datum}
                            statementIdEnd={statement_id_end}
                            statementIdStart={statement_id_start}
                            urlParamKey={URL_PARAMS.WRITER}
                        />
                    ),
                )}
            </DataCardTable>
        </DataCard>
    );
};
